import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, CheckCircle, Loader2, Factory } from 'lucide-react';

const ACOES_CRITICAS = [
  { value: 'descartar_lote', label: 'Descartar lote' },
  { value: 'reprocessamento', label: 'Reprocessamento' },
  { value: 'aprovar_lote', label: 'Aprovar lote' }
];

const NaoConformidadesProducaoPage = () => {
  const { getEmpresaId, hasPermission, userData } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [formCritico, setFormCritico] = useState({});
  const [formCiencia, setFormCiencia] = useState({});

  const podeAcaoCritica = ['superadmin', 'admin', 'administrador', 'gerente', 'qualidade'].includes(
    userData?.funcao
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const fn = httpsCallable(functions, 'listNaoConformidadesProducao');
      const result = await fn({ empresa_id: getEmpresaId() });
      if (result.data?.success) {
        setItems(result.data.data?.items || []);
      } else {
        setError(result.data?.message || 'Erro ao carregar');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Erro ao carregar não conformidades');
    } finally {
      setLoading(false);
    }
  }, [getEmpresaId]);

  useEffect(() => {
    load();
  }, [load]);

  const criticas = items.filter((i) => i.critico && i.status_registro === 'pendente_critico');
  const naoCriticas = items.filter((i) => !i.critico && i.status_registro === 'pendente_ciencia');

  const handleCiencia = async (id) => {
    const obs = formCiencia[id] || '';
    try {
      setSavingId(id);
      setError('');
      const fn = httpsCallable(functions, 'registrarCienciaNaoConformidade');
      await fn({ registro_id: id, observacao: obs });
      setSuccess('Ciência registrada.');
      setTimeout(() => setSuccess(''), 3000);
      setFormCiencia((p) => ({ ...p, [id]: '' }));
      await load();
    } catch (e) {
      setError(e.message || 'Erro ao registrar ciência');
    } finally {
      setSavingId(null);
    }
  };

  const handleAcaoCritica = async (id) => {
    const f = formCritico[id] || {};
    const acao = f.acao_tomada;
    const obs = (f.observacao_responsavel || '').trim();
    if (!acao) {
      setError('Selecione a ação tomada.');
      return;
    }
    if (obs.length < 10) {
      setError('Descreva a ação em pelo menos 10 caracteres.');
      return;
    }
    try {
      setSavingId(id);
      setError('');
      const fn = httpsCallable(functions, 'registrarAcaoNaoConformidadeCritica');
      await fn({ registro_id: id, acao_tomada: acao, observacao_responsavel: obs });
      setSuccess('Ação técnica registrada.');
      setTimeout(() => setSuccess(''), 3000);
      setFormCritico((p) => ({ ...p, [id]: {} }));
      await load();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSavingId(null);
    }
  };

  const formatarData = (ts) => {
    if (!ts) return '—';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
    } catch {
      return '—';
    }
  };

  if (!hasPermission('view_production_nonconformities')) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Sem permissão para esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
            Não conformidades — parâmetros fora do padrão
          </h1>
          <p className="text-gray-600 mt-1">
            Registros gerados quando a produção segue com desvio (parâmetro fora do range). Críticos exigem decisão
            documentada; demais apenas ciência.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          {success}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600 py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
          Carregando…
        </div>
      ) : (
        <>
          <section className="bg-white rounded-lg border border-red-200 shadow-sm overflow-hidden">
            <div className="bg-red-50 px-4 py-3 border-b border-red-200">
              <h2 className="font-semibold text-red-900">Parâmetros críticos — avaliação obrigatória</h2>
              <p className="text-xs text-red-800 mt-1">
                O responsável deve registrar a ação (descarte, reprocessamento ou aprovação do lote) e observação
                detalhada.
              </p>
            </div>
            <div className="p-4 space-y-4">
              {criticas.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum registro pendente.</p>
              ) : (
                criticas.map((row) => (
                  <div key={row.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-800 mb-2">
                      <span className="font-medium flex items-center gap-1">
                        <Factory className="h-4 w-4" />
                        {row.produto_nome}
                      </span>
                      <span>Lote: {row.lote_fabricacao}</span>
                      <span>
                        Etapa {row.etapa_numero}: {row.etapa_nome}
                      </span>
                      <span className="text-gray-500">{formatarData(row.criado_em)}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{row.parametro_nome}</p>
                    <p className="text-sm text-red-800 mb-3">{row.mensagem}</p>
                    <p className="text-xs text-gray-600">
                      Valor: {row.valor_medido ?? '—'} {row.unidade || ''} | Esperado: {row.min_esperado ?? '—'} a{' '}
                      {row.max_esperado ?? '—'} {row.unidade || ''}
                    </p>
                    {podeAcaoCritica ? (
                      <div className="mt-4 space-y-3 border-t pt-3">
                        <label className="block text-xs font-medium text-gray-700">Ação tomada *</label>
                        <select
                          className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-sm"
                          value={formCritico[row.id]?.acao_tomada || ''}
                          onChange={(e) =>
                            setFormCritico((p) => ({
                              ...p,
                              [row.id]: { ...p[row.id], acao_tomada: e.target.value }
                            }))
                          }
                        >
                          <option value="">Selecione…</option>
                          {ACOES_CRITICAS.map((a) => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </select>
                        <label className="block text-xs font-medium text-gray-700">
                          Observação detalhada (mín. 10 caracteres) *
                        </label>
                        <textarea
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[88px]"
                          placeholder="Descreva o que será feito e o embasamento técnico…"
                          value={formCritico[row.id]?.observacao_responsavel || ''}
                          onChange={(e) =>
                            setFormCritico((p) => ({
                              ...p,
                              [row.id]: { ...p[row.id], observacao_responsavel: e.target.value }
                            }))
                          }
                        />
                        <button
                          type="button"
                          disabled={savingId === row.id}
                          onClick={() => handleAcaoCritica(row.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          {savingId === row.id ? 'Salvando…' : 'Registrar ação técnica'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-800 mt-2">
                        Aguardando avaliação de gerência / qualidade.
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="bg-white rounded-lg border border-amber-200 shadow-sm overflow-hidden">
            <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
              <h2 className="font-semibold text-amber-900">Parâmetros não críticos — apenas ciência</h2>
              <p className="text-xs text-amber-900 mt-1">
                Registre o tomar ciência. Observação é opcional.
              </p>
            </div>
            <div className="p-4 space-y-4">
              {naoCriticas.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhum registro pendente.</p>
              ) : (
                naoCriticas.map((row) => (
                  <div key={row.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-800 mb-2">
                      <span className="font-medium">{row.produto_nome}</span>
                      <span>Lote: {row.lote_fabricacao}</span>
                      <span>
                        Etapa {row.etapa_numero}: {row.etapa_nome}
                      </span>
                      <span className="text-gray-500">{formatarData(row.criado_em)}</span>
                    </div>
                    <p className="text-sm text-gray-800 mb-2">{row.mensagem}</p>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[64px] mb-2"
                      placeholder="Observação opcional…"
                      value={formCiencia[row.id] || ''}
                      onChange={(e) => setFormCiencia((p) => ({ ...p, [row.id]: e.target.value }))}
                    />
                    <button
                      type="button"
                      disabled={savingId === row.id}
                      onClick={() => handleCiencia(row.id)}
                      className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                    >
                      {savingId === row.id ? 'Salvando…' : 'Dar ciência'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default NaoConformidadesProducaoPage;
